import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { hasAnyRole, ROLES } from '@/auth/roles'
import { useSyncStatus } from '@/offline'
import { useToast } from '@/ui'
import { DoneScreen } from '@/features/store/v1/bits'
import type { TagEntry } from '../commands'
import { useSupervisorPin } from '../components/SupervisorPin'
import { retag } from '../data/actions'
import { useGate } from '../data/useGate'
import type { GuardContext } from '../model/scanGuard'
import {
  Btn,
  Cta,
  Footer,
  Icon,
  ItemRow,
  Strip,
  Tick,
  V1Header,
  V1Screen,
  WaitCta,
  Warn,
} from './kit'
import { LiveScanner, Refused, TypeCodeSheet } from './scan'
import { useCheckedScan } from './scanCheck'
import { C } from './style'
import { shortCode, shortLot, tr } from './text'
import { retagWaiting, type RetagWaiting } from './retagWaiting'

const whatLabel = (w: RetagWaiting) =>
  tr(w.target === 'BAG' ? 'gate.v1.retag.bag' : 'gate.v1.retag.sample', {
    lot: w.lot_code ? shortLot(w.lot_code) : '—',
  })

/**
 * F4.6-h — नया टैग (V1): the supervisor puts a new tag or seal on a bag or sample the server kept
 * without one. Pick it, scan the new code, save (the supervisor's PIN, then the retag command).
 * Same list, tag guard and command as the full RetagPage, on the boards' look.
 */
export function V1Retag() {
  const navigate = useNavigate()
  const actor = useActor()
  const toast = useToast()
  const g = useGate()
  const sync = useSyncStatus()
  const sup = useSupervisorPin()
  const [pickedKey, setPickedKey] = useState<string | null>(null)
  const [code, setCode] = useState<{ payload: string; entry: TagEntry } | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [typing, setTyping] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState<string | null>(null)
  const [allDone, setAllDone] = useState<string | null>(null)
  const home = () => navigate('/m/gate')

  const waiting = useMemo(() => retagWaiting(g.outbox), [g.outbox])
  const picked = waiting.find((w) => w.key === pickedKey) ?? null
  const roster = g.roster
  const guard = useMemo<GuardContext | null>(
    () =>
      roster && picked
        ? {
            kind: picked.target === 'BAG' ? 'BAG' : 'SEAL',
            operatorId: actor.user_id,
            rolls: roster.rolls,
            confirmedBatchIds: new Set(roster.rolls.map((r) => r.batch_id)),
            boundPayloads: g.used.bound,
            voidedPayloads: g.used.voided,
            inDraft: new Set(),
          }
        : null,
    [roster, picked, actor.user_id, g.used],
  )
  const check = useCheckedScan({
    guard: guard ?? {
      kind: 'BAG',
      operatorId: actor.user_id,
      rolls: [],
      confirmedBatchIds: new Set(),
      boundPayloads: new Set(),
      voidedPayloads: new Set(),
      inDraft: new Set(),
    },
    onAccept: (payload, entry) => setCode({ payload, entry }),
  })

  if (allDone)
    return (
      <DoneScreen
        title={tr('gate.v1.retag.doneTitle')}
        lines={tr('gate.v1.retag.doneLine', { code: shortCode(allDone) })}
        chips={[]}
        onHome={home}
      />
    )
  if (!g.ready || !roster) return <V1Screen />
  const isSup = hasAnyRole(actor.roles, [ROLES.SUPERVISOR])
  const isBag = picked?.target !== 'SAMPLE'

  const back = () => {
    setPickedKey(null)
    setCode(null)
    setErr(null)
  }

  const save = async () => {
    if (!picked || !code) return
    const s = await sup.request(tr('gate.v1.retag.supReason'))
    if (!s) return
    setSaving(true)
    try {
      await retag(g.app, s.user_id, {
        target: picked.target,
        container_id: picked.container_id,
        sample_id: picked.sample_id,
        qr_payload: code.payload,
        entry: code.entry,
      })
      const left = waiting.filter((w) => w.key !== picked.key).length
      back()
      if (left === 0) setAllDone(code.payload)
      else setSaved(code.payload)
    } catch (e) {
      toast.error(tr('gate.v1.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  // Step 2: scan the new tag (or seal) for the picked bag or sample.
  if (picked)
    return (
      <>
        <V1Screen
          main={{ gap: 9 }}
          header={
            <V1Header
              onBack={back}
              pending={sync.pending}
              icon={isBag ? 'tag' : 'seal'}
              title={tr(isBag ? 'gate.v1.retag.scanTag' : 'gate.v1.retag.scanSeal')}
            />
          }
          footer={
            <Footer>
              {code ? (
                <Cta
                  label={tr(isBag ? 'gate.v1.retag.saveTag' : 'gate.v1.retag.saveSeal')}
                  icon="ok"
                  busy={saving}
                  onClick={() => void save()}
                />
              ) : (
                <WaitCta>{tr(isBag ? 'gate.v1.retag.holdTag' : 'gate.v1.retag.holdSeal')}</WaitCta>
              )}
            </Footer>
          }
          overlay={
            typing ? (
              <TypeCodeSheet
                title={tr(isBag ? 'gate.v1.tag.typeTitle' : 'gate.v1.seal.typeTitle')}
                check={check}
                onClose={() => setTyping(false)}
              />
            ) : undefined
          }
        >
          <ItemRow
            padding="8px 10px"
            lead={<LeadIcon icon={isBag ? 'sack' : 'seal'} />}
            title={whatLabel(picked)}
            line={
              picked.conflict ? (
                <span style={{ color: C.muted }}>
                  {tr('gate.v1.retag.conflict', { code: shortCode(picked.conflict) })}
                </span>
              ) : undefined
            }
          />
          {!isSup && <Warn>{tr('gate.v1.retag.supOnly')}</Warn>}
          {code ? (
            <ItemRow
              padding="8px 10px"
              border={C.ok}
              borderWidth={2}
              lead={<Tick size={28} />}
              title={tr(isBag ? 'gate.v1.retag.tagScanned' : 'gate.v1.retag.sealScanned', {
                code: shortCode(code.payload),
              })}
              right={
                <Btn icon="undo" onClick={() => setCode(null)}>
                  {tr('gate.v1.retag.again')}
                </Btn>
              }
            />
          ) : (
            <>
              {!typing && !sup.sheet && (
                <LiveScanner
                  icon={isBag ? 'tag' : 'seal'}
                  hint={tr(isBag ? 'gate.v1.retag.holdTag' : 'gate.v1.retag.holdSeal')}
                  check={check}
                  onError={setErr}
                />
              )}
              {err && <Refused text={err} />}
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn icon="keypad" onClick={() => setTyping(true)}>
                  {tr('gate.v1.tag.type')}
                </Btn>
              </div>
            </>
          )}
        </V1Screen>
        {sup.sheet}
      </>
    )

  // Step 1: what waits for a tag.
  return (
    <V1Screen
      header={
        <V1Header
          onBack={home}
          pending={sync.pending}
          icon="tag"
          title={tr('gate.v1.retag.title')}
        />
      }
      footer={
        <Footer>
          {waiting.length > 0 ? (
            <WaitCta>{tr('gate.v1.retag.pick')}</WaitCta>
          ) : (
            <Cta label={tr('gate.v1.retag.home')} icon="home" lead onClick={home} />
          )}
        </Footer>
      }
    >
      {saved && (
        <div
          role="status"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            borderRadius: 12,
            background: C.ok,
            color: '#ffffff',
            fontSize: 17,
            fontWeight: 700,
          }}
        >
          <Icon name="ok" size={22} sw={3} />
          {tr('gate.v1.retag.saved', { code: shortCode(saved) })}
        </div>
      )}
      {!isSup && <Warn>{tr('gate.v1.retag.supOnly')}</Warn>}
      {waiting.length === 0 ? (
        <>
          <Strip icon="ok" iconBox>
            {tr('gate.v1.retag.none')}
          </Strip>
          <p style={{ margin: 0, fontSize: 16, lineHeight: 1.4, color: C.muted }}>
            {tr('gate.v1.retag.noneHint')}
          </p>
        </>
      ) : (
        <>
          <Strip icon="tag">{tr('gate.v1.retag.count', { count: waiting.length })}</Strip>
          {waiting.map((w) => (
            <ItemRow
              key={w.key}
              padding="8px 10px"
              onClick={() => {
                setSaved(null)
                setErr(null)
                setCode(null)
                setPickedKey(w.key)
              }}
              lead={<LeadIcon icon={w.target === 'BAG' ? 'sack' : 'seal'} />}
              title={whatLabel(w)}
              line={
                w.conflict ? (
                  <span style={{ color: C.muted }}>
                    {tr('gate.v1.retag.conflict', { code: shortCode(w.conflict) })}
                  </span>
                ) : undefined
              }
              right={
                <span style={{ color: C.acc, display: 'flex' }}>
                  <Icon name="next" size={24} sw={2.5} />
                </span>
              }
            />
          ))}
        </>
      )}
    </V1Screen>
  )
}

function LeadIcon({ icon }: { icon: 'sack' | 'seal' }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 44,
        height: 44,
        flexShrink: 0,
        borderRadius: 10,
        background: C.warnWash,
        color: C.accDark,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon name={icon} size={28} />
    </span>
  )
}
